/**
 * Coordinates token refresh for API calls that receive a 401.
 *
 * When an API call gets a 401, it calls requestToken() which sends REQUEST_TOKEN
 * to the parent frame and returns a Promise that resolves when the new token
 * arrives. Multiple concurrent 401s share a single REQUEST_TOKEN message and
 * all resolve together when auth-context calls notifyTokenUpdate().
 */
const REQUEST_TOKEN_TIMEOUT_MS = 10_000

class TokenManager {
  private pendingResolvers: ((token: string) => void)[] = []
  private tokenRequested = false
  private latestToken: string | null = null

  getToken(): string | null {
    return this.latestToken
  }

  requestToken(): Promise<string> {
    if (!this.tokenRequested) {
      this.tokenRequested = true
      if (typeof window !== "undefined") {
        window.parent.postMessage({ action: "REQUEST_TOKEN" }, "*")
      }
    }
    return new Promise<string>((resolve, reject) => {
      let timer: ReturnType<typeof setTimeout> | null = null
      const wrappedResolve = (token: string) => {
        if (timer !== null) clearTimeout(timer)
        resolve(token)
      }
      this.pendingResolvers.push(wrappedResolve)
      timer = setTimeout(() => {
        this.pendingResolvers = this.pendingResolvers.filter((r) => r !== wrappedResolve)
        if (this.pendingResolvers.length === 0) {
          this.tokenRequested = false
        }
        reject(new Error("Token request timed out"))
      }, REQUEST_TOKEN_TIMEOUT_MS)
    })
  }

  notifyTokenUpdate(token: string) {
    this.latestToken = token
    this.tokenRequested = false
    const resolvers = [...this.pendingResolvers]
    this.pendingResolvers = []
    resolvers.forEach((resolve) => resolve(token))
  }
}

export const tokenManager = new TokenManager()
